import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  TablePagination,
  Typography,
} from '@mui/material';
import { AddShoppingCart as AddShoppingCartIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../common/ConfirmDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareDateRangePicker from '../common/SalovareDateRangePicker';
import SalovareSelectField from '../common/SalovareSelectField';
import CustomerDropdown from '../customers/CustomerDropdown';
import { FIELD_PLACEHOLDER_COLOR } from '../common/formFieldStyles';
import ProductSaleDetailsDialog from './ProductSaleDetailsDialog';
import ProductSaleService from '../../services/productSaleService';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import {
  formatProductSaleItemsSummary,
  getProductSaleAmounts,
} from '../../utils/productSaleItemsUtils';
import { getProductSaleStatusChipSx, getProductSaleStatusKey, PRODUCT_SALE_STATUS } from '../../utils/productSaleStatusUtils';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const CHANNELS = {
  StaffInStore: 'StaffInStore',
  CustomerSelfService: 'CustomerSelfService',
};

const toDateOnly = (value) => {
  if (!value) {
    return undefined;
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const startOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const ProductSaleAmount = ({ sale, currency }) => {
  const { original, paid, hasDiscount } = getProductSaleAmounts(sale);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-end', sm: 'baseline' },
        justifyContent: 'flex-end',
        gap: { xs: 0.15, sm: 1 },
        minWidth: 0,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND.accentText, lineHeight: 1.2 }}>
        {formatCurrency(paid, currency)}
      </Typography>
      {hasDiscount ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            textDecoration: 'line-through',
            fontWeight: 500,
            lineHeight: 1.2,
          }}
        >
          {formatCurrency(original, currency)}
        </Typography>
      ) : null}
    </Box>
  );
};

const ProductSaleListItem = ({
  sale,
  currency,
  statusLabel,
  itemsLabel,
  professionalLabel,
  customerLabel,
  onClick,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: 'block',
      width: '100%',
      textAlign: 'left',
      border: `1px solid ${BRAND.line}`,
      borderRadius: 2,
      p: 2,
      bgcolor: '#fff',
      cursor: 'pointer',
      font: 'inherit',
      color: 'inherit',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '&:hover': {
        borderColor: BRAND.accent,
        boxShadow: `0 8px 20px ${brandAlpha('primary', 0.08)}`,
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 0.75 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: BRAND.text,
          lineHeight: 1.35,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {itemsLabel}
      </Typography>
      <Chip size="small" label={statusLabel} sx={getProductSaleStatusChipSx(sale.status)} />
    </Box>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mb: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {professionalLabel}
      {' · '}
      {customerLabel}
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'flex-end' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {formatUtcDate(sale.soldAtUtc || sale.saleDate)}
      </Typography>
      <ProductSaleAmount sale={sale} currency={currency} />
    </Box>
  </Box>
);

const ProductSalesPanel = ({
  storeId,
  teamMembers = [],
  currency,
  canVoid = false,
  canCreateSales = false,
  onRecordSale,
  refreshKey = 0,
}) => {
  const { t, i18n } = useTranslation();
  const [from, setFrom] = useState(() => startOfMonth());
  const [to, setTo] = useState(() => new Date());
  const [teamMemberId, setTeamMemberId] = useState('');
  const [customer, setCustomer] = useState(null);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [sales, setSales] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleToVoid, setSaleToVoid] = useState(null);
  const [voiding, setVoiding] = useState(false);

  const customerId = customer?.customerId || '';

  const loadSales = useCallback(async () => {
    if (!storeId) {
      setLoading(false);
      return;
    }

    const requestKey = [
      'getProductSales',
      storeId,
      toDateOnly(from) || '',
      toDateOnly(to) || '',
      teamMemberId || '',
      customerId || '',
      pageIndex,
      itemsPerPage,
      refreshKey,
    ].join('-');

    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setLoading(true);
        setErrorKey(null);
        const result = await ProductSaleService.getProductSales({
          storeId,
          from: toDateOnly(from),
          to: toDateOnly(to),
          teamMemberId: teamMemberId || undefined,
          customerId: customerId || undefined,
          pageIndex,
          itemsPerPage,
        });
        setSales(result.data);
        setTotalItems(result.totalItems);
      } catch (error) {
        console.error('Failed to load product sales:', error);
        setErrorKey('productsManagement.error.loading');
      } finally {
        setLoading(false);
      }
    });
  }, [storeId, from, to, teamMemberId, customerId, pageIndex, itemsPerPage, refreshKey]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  const handleVoid = async () => {
    if (!saleToVoid) {
      return;
    }

    try {
      setVoiding(true);
      await ProductSaleService.voidProductSale(storeId, saleToVoid.id);
      setSaleToVoid(null);
      setSelectedSale(null);
      await loadSales();
    } catch (error) {
      console.error('Failed to void product sale:', error);
      setErrorKey('productsManagement.error.voidSale');
    } finally {
      setVoiding(false);
    }
  };

  const channelLabel = useCallback((value) => {
    if (value === CHANNELS.CustomerSelfService || value === 2) {
      return t('productsManagement.sales.channels.customerSelfService');
    }
    return t('productsManagement.sales.channels.staffInStore');
  }, [t]);

  const statusLabel = (value) => {
    const key = getProductSaleStatusKey(value);
    if (key === PRODUCT_SALE_STATUS.Voided) {
      return t('productsManagement.sales.statuses.voided');
    }
    if (key === PRODUCT_SALE_STATUS.Pending) {
      return t('productsManagement.sales.statuses.pending');
    }
    return t('productsManagement.sales.statuses.completed');
  };

  const canVoidSale = (sale) => {
    if (!sale) {
      return false;
    }
    return canVoid && getProductSaleStatusKey(sale.status) === PRODUCT_SALE_STATUS.Completed;
  };

  const filterRow = useMemo(() => (
    <Box sx={{ mb: 3 }}>
      {canCreateSales && onRecordSale ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            mb: { xs: 2.5, md: 4 },
            pt: { xs: 0.5, md: 1 },
          }}
        >
          <PrimaryActionButton
            startIcon={<AddShoppingCartIcon />}
            onClick={onRecordSale}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              px: { xs: 2, sm: 4 },
              py: 1.5,
            }}
          >
            {t('productsManagement.recordSale')}
          </PrimaryActionButton>
        </Box>
      ) : null}

      <SalovareDateRangePicker
        startValue={from}
        endValue={to}
        onStartChange={(value) => {
          setPageIndex(0);
          setFrom(value);
        }}
        onEndChange={(value) => {
          setPageIndex(0);
          setTo(value);
        }}
        helperContent={(
          <Button
            color="inherit"
            onClick={() => setAdvancedSearchOpen((open) => !open)}
            sx={{
              color: BRAND.accentText,
              fontWeight: 600,
              px: 0,
              minWidth: 0,
              minHeight: 22,
              py: 0,
              textTransform: 'none',
              justifyContent: 'flex-start',
            }}
          >
            {advancedSearchOpen
              ? t('productsManagement.sales.hideAdvancedSearch')
              : t('productsManagement.sales.advancedSearch')}
          </Button>
        )}
      />

      {advancedSearchOpen ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mt: 0.5,
          }}
        >
          <SalovareSelectField
            label={t('productsManagement.sale.professional')}
            value={teamMemberId}
            onChange={(event) => {
              setPageIndex(0);
              setTeamMemberId(event.target.value);
            }}
            displayEmpty
            renderValue={(value) => value
              ? (teamMembers.find((member) => member.id === value)?.name || value)
              : (
                <Typography component="span" sx={{ color: FIELD_PLACEHOLDER_COLOR }}>
                  {t('productsManagement.sale.professional')}
                </Typography>
              )}
          >
            <MenuItem value="">
              <Typography sx={{ color: FIELD_PLACEHOLDER_COLOR }}>{t('productsManagement.sale.professional')}</Typography>
            </MenuItem>
            {teamMembers.map((member) => (
              <MenuItem key={member.id} value={member.id}>{member.name}</MenuItem>
            ))}
          </SalovareSelectField>
          <CustomerDropdown
            storeId={storeId}
            label={t('productsManagement.sale.customer')}
            placeholder={t('productsManagement.sale.customer')}
            value={customer}
            onChange={(nextCustomer) => {
              setPageIndex(0);
              setCustomer(nextCustomer);
            }}
          />
        </Box>
      ) : null}
    </Box>
  ), [
    from,
    to,
    teamMemberId,
    customer,
    advancedSearchOpen,
    teamMembers,
    storeId,
    canCreateSales,
    onRecordSale,
    t,
  ]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {filterRow}

      {errorKey ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(errorKey)}
          <Button onClick={loadSales} sx={{ ml: 1 }}>{t('common.retry')}</Button>
        </Alert>
      ) : null}

      {!loading && sales.length === 0 ? (
        <Typography color="text.secondary">{t('productsManagement.sales.empty')}</Typography>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {sales.map((sale) => (
              <ProductSaleListItem
                key={sale.id}
                sale={sale}
                currency={currency}
                statusLabel={statusLabel(sale.status)}
                itemsLabel={formatProductSaleItemsSummary(sale, i18n.language)}
                professionalLabel={sale.teamMemberName || t('productsManagement.sale.noProfessional')}
                customerLabel={sale.customerName || t('productsManagement.sale.walkIn')}
                onClick={() => setSelectedSale(sale)}
              />
            ))}
          </Box>
          <TablePagination
            component="div"
            count={totalItems}
            page={pageIndex}
            onPageChange={(_, nextPage) => setPageIndex(nextPage)}
            rowsPerPage={itemsPerPage}
            onRowsPerPageChange={(event) => {
              setItemsPerPage(Number(event.target.value));
              setPageIndex(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </Box>
      )}

      <ProductSaleDetailsDialog
        open={Boolean(selectedSale)}
        sale={selectedSale}
        currency={currency}
        canVoid={canVoidSale(selectedSale)}
        voiding={voiding}
        channelLabel={channelLabel}
        statusLabel={statusLabel}
        onClose={() => setSelectedSale(null)}
        onVoid={() => setSaleToVoid(selectedSale)}
      />

      <ConfirmDialog
        open={Boolean(saleToVoid)}
        onClose={() => setSaleToVoid(null)}
        onConfirm={handleVoid}
        title={t('productsManagement.sales.void')}
        message={t('productsManagement.sales.voidConfirm')}
        confirmText={t('productsManagement.sales.void')}
        cancelText={t('common.cancel')}
        loading={voiding}
      />
    </Box>
  );
};

export default ProductSalesPanel;
